import * as React from 'react';
import { Link } from 'react-router-dom';
import { ButtonTertiary, MeatballMenu } from 'ui';
import { MdRefresh } from 'react-icons/md';
import { DashboardLayout } from 'app/area/dashboard/dashboard-layout';
import { useActionCreators, useDashboardExecuteEffect, useIsDashboardDisabled, useReportApiOverrides } from 'app/area/dashboard/hooks';
import { DashboardAccessType } from 'app/area/dashboard/types';
import { DashboardLayoutDefinition } from 'app/area/dashboard/types-store';
import {
  useFavouriteDefinitions, useLoadFavouriteDefinitionEffect, useSavedReportState,
  useSearchRequiredUsers
} from 'app/area/favourite/hooks';
import { FiltersDrawer } from 'app/area/filters/filters-drawer';
import { CreateReport } from 'app/area/saved-reports/create-report';
import { SpinLoader } from 'app/components';
import { DrillDownModel } from 'app/components/favourite-layout/favourite/common/drill-down-model';
import { triggerPrintDialogForDashboards } from 'app/infrastructure/platform';
import { useSharedState } from 'app/infrastructure/redux/hooks';
import { routeConstants } from 'app/routes/route-constants';
import { FavouriteDefinition, SharedState } from 'app/types-shared';
import { MainGridContainer } from 'ui/common/main-grid-container/main-grid-container';
import styles from './dashboard.scss';

interface DashboardFavouriteProps {
  translation: any;
  dashboardType: number;
  mainFavouriteId: number;
  showDescription: boolean;
  hideFilters?: boolean;
  hideFilterReplacements?: boolean;
  fetchingMainFavourite: boolean;
  containerLayout: DashboardLayoutDefinition;
  drillDown: (favouriteId: number, model: DrillDownModel) => void;
  sharedState: SharedState;
  editSavedFavourite: boolean;
}

export function DashboardFavourite({
  translation,
  dashboardType,
  mainFavouriteId,
  showDescription,
  hideFilters,
  hideFilterReplacements,
  fetchingMainFavourite,
  containerLayout,
  drillDown,
  sharedState,
  editSavedFavourite
}: DashboardFavouriteProps) {

  const {
    definition,
    workingDefinition,
    updateDefinition
  } = useFavouriteDefinitions();

  const {
    createReport,
    updateExistingReport,
    onCloseReport,
    onCreateReport,
    onUpdateReport
  } = useSavedReportState(editSavedFavourite, false);

  useLoadFavouriteDefinitionEffect(mainFavouriteId, true);

  const executionCounter = useDashboardExecuteEffect(workingDefinition);

  const reportApiOverrides = useReportApiOverrides(mainFavouriteId);

  const searchRequiredUsers = useSearchRequiredUsers();
  const dashboardIsDisabled = useIsDashboardDisabled(definition);
  const showUpdate = definition?.validState?.owners?.some(owner => owner.v === sharedState?.id);

  const description = dashboardType > 0 ?
    (showDescription ?
      <DashboardDescription
        dashboardType={dashboardType}
        mainFavouriteId={mainFavouriteId}
        containerLayout={containerLayout}
        translation={translation}
        onCreateReport={onCreateReport}
        onUpdateReport={onUpdateReport}
        onPrint={triggerPrintDialogForDashboards}
        showUpdate={showUpdate}
        definition={definition} /> :
      null
    ) : translation.dashboard_not_render_description;

  const dashboardFavourites = (!fetchingMainFavourite && containerLayout && containerLayout.dashboardFavourites) || [];

  const createDashboard = createReport &&
    <CreateReport
      closeReportCreator={onCloseReport}
      definition={definition}
      searchRequiredUsers={searchRequiredUsers}
      updateReport={updateExistingReport}
      className={styles.createReportSection}
      update_report_title={translation.update_existing_dashboard}
      title={translation.save_new_dashboard}
      reportIsDisabled={dashboardIsDisabled} />;

  const favouritesLayout = <div className={styles.favouritesWrapper}>
    <DashboardLayout
      mainFavouriteId={mainFavouriteId}
      reportApiOverrides={reportApiOverrides}
      executionCounter={executionCounter}
      dashboardFavourites={dashboardFavourites}
      openSavedReportTitle={translation.open_saved_report}
      drillDown={drillDown}
    />
  </div>;

  const hideDisclaimer = containerLayout?.dashboardAccessType?.v === DashboardAccessType.OpenDashboard;

  return (
    <MainGridContainer className={`${styles.dashboard} dashboard`} wide={!hideFilters}>
      {description}
      {createDashboard}
      {hideFilters ?
        favouritesLayout
        : <FiltersDrawer
          title={translation.filters_title}
          definition={definition}
          workingDefinition={workingDefinition}
          updateDefinition={updateDefinition}
          hideDisclaimerMessage={hideDisclaimer}
          hideFilterReplacements={hideFilterReplacements}>
          {favouritesLayout}
        </FiltersDrawer>}
    </MainGridContainer>
  );
}

function DashboardDescription({
  dashboardType,
  mainFavouriteId,
  containerLayout,
  translation,
  onPrint,
  onCreateReport,
  onUpdateReport,
  showUpdate,
  definition
}: {
  dashboardType: number;
  mainFavouriteId: number;
  containerLayout: DashboardLayoutDefinition;
  translation: any;
  onPrint: () => void;
  onCreateReport: () => void;
  onUpdateReport: () => void;
  showUpdate: boolean;
  definition: FavouriteDefinition;
}) {
  const sharedState = useSharedState();
  const dashboardAdmin = sharedState.access?.adminPanel?.dashboard?.administrationCentral ?? false;
  const dashboardDescription = definition?.validState?.validFavourite?.d;

  const { executeDashboard } = useActionCreators();

  return (
    <div className={styles.headerContainer}>
      <h1 className={styles.header}>
        {containerLayout ? containerLayout.description : <SpinLoader />}
      </h1>

      <h3>
        {dashboardDescription}
      </h3>

      <ButtonTertiary classNames={styles.refreshDashboard} icon={MdRefresh} onClick={executeDashboard}>
        {translation.refresh_dashboard}
      </ButtonTertiary>

      <MeatballMenu closeAfterClick={true}>
        <li>
          <button type="button" className="dropdown-item" onClick={onCreateReport}>
            {translation.save_new_dashboard}
          </button>
        </li>
        {showUpdate && <li>
          <button type="button" className="dropdown-item" onClick={onUpdateReport}>
            {translation.update_existing_dashboard}
          </button>
        </li>}
        {dashboardAdmin && <>
          <li>
            <Link className="dropdown-item" to={routeConstants.dashboardAdminItemFor(dashboardType, mainFavouriteId)}>
              {translation.edit_dashboard}
            </Link>
          </li>
          <li>
            <Link className="dropdown-item" to={routeConstants.dashboardAdminSavedReportsFor(dashboardType, mainFavouriteId)}>
              {translation.edit_saved_reports}
            </Link>
          </li>
          <li>
            <Link className="dropdown-item" to={routeConstants.dashboardAdminList}>
              {translation.manage_dashboards_option}
            </Link>
          </li>
        </>}
        <li>
          <button type="button" className="dropdown-item" onClick={onPrint}>
            {translation.print}
          </button>
        </li>
      </MeatballMenu>
    </div>
  );
}
